import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { useHistory } from 'react-router-dom';

import * as baseActions from '../../actions/base';
import * as orgActions from '../../actions/org';
import * as syncBackendActions from '../../actions/sync_backend';

import ChoiceModal from '../UI/ChoiceModal';

import { directoryOfPath, filePathToUrl } from '../../lib/dashboard';
import {
  concatenateSnippets,
  DAYSTART_FAST,
  DAYSTART_SLOW,
  daystartFolder,
  fileExists,
  listDaystartSnippets,
  snippetLabel,
  snippetsAreAvailable,
  snippetsProjectId,
} from '../../lib/snippets';

/**
 * Opens a daily note, and offers to create it from snippets when it does not exist.
 *
 * Mounted with a `targetPath` by whoever wants to go to a daily note (the action
 * drawer's "today" button, the dashboard tile). It checks for the file first and
 * navigates straight there when it exists, so the prompt only ever appears for a
 * day that has not been started yet.
 */
const DaystartPrompt = ({
  targetPath,
  onClose,
  base,
  org,
  syncBackend,
  syncBackendClient,
  snippetsRepositoryUrl,
  fastDaystartFolder,
  slowDaystartFolder,
}) => {
  const history = useHistory();

  // 'checking' -> 'asking' -> 'picking' -> 'creating'. Nothing is rendered while
  // checking, so an existing daily note opens without a modal flashing up.
  const [phase, setPhase] = useState('checking');
  // The snippets of the chosen daystart, once its folder has been listed.
  const [snippets, setSnippets] = useState([]);
  const [chosenKind, setChosenKind] = useState(null);

  const projectId = snippetsProjectId(snippetsRepositoryUrl);
  const canUseSnippets = snippetsAreAvailable(syncBackendClient, snippetsRepositoryUrl);

  const go = useCallback(() => {
    onClose();
    history.push(filePathToUrl(targetPath));
  }, [history, onClose, targetPath]);

  useEffect(() => {
    let isCurrent = true;

    const check = async () => {
      if (!syncBackendClient) {
        base.setDisappearingLoadingMessage('Not connected to a sync backend.', 2000);
        onClose();
        return;
      }

      if (await fileExists(syncBackendClient, targetPath)) {
        if (isCurrent) go();
        return;
      }

      if (!isCurrent) return;

      // Without a snippet repository there is nothing to offer, so say what is
      // missing instead of showing a menu with no usable options.
      if (!canUseSnippets) {
        base.setDisappearingLoadingMessage(
          `${targetPath} does not exist yet. Configure a snippet repository in Settings > Snippets to start a day from it.`,
          4000
        );
        onClose();
        return;
      }

      setPhase('asking');
    };

    check();

    return () => {
      isCurrent = false;
    };
  }, [base, canUseSnippets, go, onClose, syncBackendClient, targetPath]);

  // Step one: which kind of day. Lists that folder so step two can offer its
  // snippets — the day is started from one of them, not from the whole folder.
  const handleChooseKind = async (kind) => {
    const configuredFolder = daystartFolder(kind, { fastDaystartFolder, slowDaystartFolder });

    if (!configuredFolder) {
      base.setDisappearingLoadingMessage(
        `No folder is configured for a ${kind} daystart. Set one in Settings > Snippets.`,
        4000
      );
      onClose();
      return;
    }

    setChosenKind(kind);
    setPhase('listing');

    try {
      const available = await listDaystartSnippets(syncBackendClient, projectId, configuredFolder);

      if (!available.length) {
        base.setDisappearingLoadingMessage(
          `No snippets match ${configuredFolder}.`,
          4000
        );
        onClose();
        return;
      }

      setSnippets(available);
      setPhase('picking');
    } catch (error) {
      console.error('Could not list the daystart snippets:', error);
      base.setDisappearingLoadingMessage(`Could not read ${configuredFolder}: ${error.message}`, 4000);
      onClose();
    }
  };

  // Step two: which snippet the new daily note starts from.
  const handleChooseSnippet = async (path) => {
    setPhase('creating');

    try {
      const raw = await syncBackendClient.getSnippetContents(projectId, path);
      // Normalises trailing whitespace and guarantees the single closing newline a
      // freshly created Org file should have.
      const contents = concatenateSnippets([raw]);

      if (!contents) {
        base.setDisappearingLoadingMessage(`${path} is empty.`, 3000);
        onClose();
        return;
      }

      // The client is called directly rather than through the createFile action,
      // because that one is fire-and-forget and we must not navigate until the
      // file really exists. The bookkeeping it does is repeated here: the new file
      // goes into the store, its sync time is stamped so the next sync does not
      // treat it as stale, and the directory listing is refreshed.
      await syncBackendClient.createFile(targetPath, contents);
      org.addNewFile(targetPath, contents);
      org.setLastSyncAt(new Date(Date.now() + 5000), targetPath);
      syncBackend.getDirectoryListing(directoryOfPath(targetPath));

      go();
    } catch (error) {
      console.error('Could not create the daily note from a snippet:', error);
      base.setDisappearingLoadingMessage(`Could not create ${targetPath}: ${error.message}`, 4000);
      onClose();
    }
  };

  if (phase === 'checking') return null;

  // Step two: pick the one snippet the day starts from. Cancelling here goes back to
  // the fast/slow question rather than abandoning the whole thing.
  if (phase === 'picking' || phase === 'creating') {
    return (
      <ChoiceModal
        title={chosenKind === DAYSTART_SLOW ? 'Slow daystart' : 'Fast daystart'}
        description={`Start ${targetPath} from which snippet?`}
        isBusy={phase === 'creating'}
        busyMessage="Creating the daily note..."
        choices={snippets.map((snippet) => ({
          key: snippet.path,
          label: snippetLabel(snippet.path),
          description: snippet.path,
          icon: chosenKind === DAYSTART_SLOW ? 'coffee' : 'bolt',
        }))}
        onChoose={handleChooseSnippet}
        onCancel={() => setPhase('asking')}
      />
    );
  }

  return (
    <ChoiceModal
      title="Start the day"
      description={`${targetPath} does not exist yet. Create it from a snippet?`}
      isBusy={phase === 'listing'}
      busyMessage="Looking for snippets..."
      choices={[
        {
          key: DAYSTART_FAST,
          label: 'Fast daystart',
          description: fastDaystartFolder || 'no folder configured',
          icon: 'bolt',
        },
        {
          key: DAYSTART_SLOW,
          label: 'Slow daystart',
          description: slowDaystartFolder || 'no folder configured',
          // 'mug-hot' only exists from FontAwesome 5.4; this project pins 5.1.1.
          icon: 'coffee',
        },
      ]}
      onChoose={handleChooseKind}
      onCancel={onClose}
    />
  );
};

const mapStateToProps = (state) => ({
  syncBackendClient: state.syncBackend.get('client'),
  snippetsRepositoryUrl: state.base.get('snippetsRepositoryUrl') || '',
  fastDaystartFolder: state.base.get('fastDaystartFolder') || '',
  slowDaystartFolder: state.base.get('slowDaystartFolder') || '',
});

const mapDispatchToProps = (dispatch) => ({
  base: bindActionCreators(baseActions, dispatch),
  org: bindActionCreators(orgActions, dispatch),
  syncBackend: bindActionCreators(syncBackendActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(DaystartPrompt);
